/**
 * Owner: Masa Bayu
 * File: hub-covers.ts
 * Date:Time: 2026-10-25 13:20
 * Description: COVER-A3 — stor cover qxp-hub dalam qxread: URL laluan cover
 *   (diisi semasa sync katalog) + pemuat dataURL (cache + langganan) untuk kad.
 *
 * Cover dihantar sebagai JSON bertextur-base64 (saluran yang sama dengan
 * underlay halaman, §423e) di luar prefix `content/`, jadi ia awam dan kad
 * tetamu tidak kena 401. DataURL dimuat malas oleh komponen; tiada fabrikasi —
 * muat gagal → null → UI kekal pada kotak nada cover.
 */

import type { QxpHubCoverSource } from './hub-descriptor';

export interface HubCoverLocation {
  readonly url: string;
  readonly source: QxpHubCoverSource;
}

/** Bentuk fetch minimum — tanpa Bearer, kerana fail cover tidak digate DRM. */
export interface HubCoverResponse {
  readonly ok: boolean;
  readonly json: () => Promise<unknown>;
}

export type HubCoverFetch = (url: string) => Promise<HubCoverResponse>;

async function defaultHubCoverFetch(url: string): Promise<HubCoverResponse> {
  return (await fetch(url)) as HubCoverResponse;
}

const locations = new Map<string, HubCoverLocation>();
const dataUrls = new Map<string, string>();
const failedUrls = new Set<string>();
const inflight = new Map<string, Promise<void>>();
const listeners = new Set<() => void>();

function notify(): void {
  for (const listener of [...listeners]) listener();
}

/** Gantikan peta lokasi cover dengan snapshot catalog terkini (panggil sebelum overlay bump). */
export function setHubCovers(entries: Readonly<Record<string, HubCoverLocation>>): void {
  locations.clear();
  for (const [publicationId, entry] of Object.entries(entries)) {
    locations.set(publicationId, entry);
  }
  notify();
}

export function getHubCoverLocation(publicationId: string): HubCoverLocation | null {
  return locations.get(publicationId.trim()) ?? null;
}

/** DataURL yang sudah dimuat (null = belum dimuat atau tiada cover). */
export function cachedHubCoverDataUrl(publicationId: string): string | null {
  const location = getHubCoverLocation(publicationId);
  if (location === null) return null;
  return dataUrls.get(location.url) ?? null;
}

/**
 * Body fail cover (`{ kind, source, mime, data, … }`) diterima hanya bila `data`
 * ialah dataURL imej; bentuk lain → null (gagal keras, bukan tekaan).
 */
export function parseHubCoverBody(body: unknown): string | null {
  if (body === null || typeof body !== 'object') return null;
  const data = (body as { data?: unknown }).data;
  return typeof data === 'string' && data.startsWith('data:image/') ? data : null;
}

/** Muat + cache dataURL cover satu terbitan. Idempoten; kegagalan tidak diulang. */
export async function loadHubCover(
  publicationId: string,
  fetchImpl: HubCoverFetch = defaultHubCoverFetch,
): Promise<string | null> {
  const location = getHubCoverLocation(publicationId);
  if (location === null) return null;
  const cached = dataUrls.get(location.url);
  if (cached !== undefined) return cached;
  if (failedUrls.has(location.url)) return null;
  const pending = inflight.get(location.url);
  if (pending !== undefined) {
    await pending;
    return dataUrls.get(location.url) ?? null;
  }
  const task = (async (): Promise<void> => {
    try {
      const res = await fetchImpl(location.url);
      if (!res.ok) {
        failedUrls.add(location.url);
        return;
      }
      const dataUrl = parseHubCoverBody(await res.json());
      if (dataUrl === null) failedUrls.add(location.url);
      else dataUrls.set(location.url, dataUrl);
    } catch {
      failedUrls.add(location.url);
    } finally {
      inflight.delete(location.url);
    }
  })();
  inflight.set(location.url, task);
  await task;
  notify();
  return dataUrls.get(location.url) ?? null;
}

/** Maklumkan langganan selepas setiap muatan/set supaya kad boleh re-render. */
export function subscribeHubCovers(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Ujian sahaja. */
export function __clearHubCoversForTests(): void {
  locations.clear();
  dataUrls.clear();
  failedUrls.clear();
  inflight.clear();
  listeners.clear();
}
